export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

const buildUrl = (path: string, params?: QueryParams): string => {
  if (!params) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "") continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${path}?${qs}` : path;
};

const parseResponse = async <T>(response: Response): Promise<T> => {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return (await response.json()) as T;
  }
  return undefined as T;
};

const formatDetail = (detail: unknown): string | undefined => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422: detail es [{loc: [...], msg: "...", type: "..."}]
    return detail
      .map((d) => {
        if (d && typeof d === "object" && "msg" in d) {
          const loc = "loc" in d && Array.isArray(d.loc) ? d.loc.join(".") : "";
          return loc ? `${loc}: ${String(d.msg)}` : String(d.msg);
        }
        return String(d);
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return undefined;
};

export const apiFetch = async <T>(
  path: string,
  options: RequestInit & { params?: QueryParams } = {},
): Promise<T> => {
  const { params, headers, ...rest } = options;
  const url = buildUrl(path, params);

  const response = await fetch(url, {
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(rest.body && !(rest.body instanceof FormData)
        ? { "Content-Type": "application/json" }
        : {}),
      ...headers,
    },
    ...rest,
  });

  if (response.status === 401) {
    window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    throw new ApiError(401, "No autenticado");
  }

  if (response.status === 403) {
    throw new ApiError(403, "No tienes permisos para esta acción");
  }

  if (!response.ok) {
    const errorBody = (await parseResponse<{ detail?: unknown }>(response).catch(
      () => ({}),
    )) as { detail?: unknown };
    throw new ApiError(response.status, formatDetail(errorBody.detail) ?? "Error de red");
  }

  return parseResponse<T>(response);
};

export const apiDownload = async (
  path: string,
  params?: QueryParams,
  filenameHint = "download.xlsx",
): Promise<void> => {
  const url = buildUrl(path, params);
  const response = await fetch(url, { credentials: "include" });
  if (!response.ok) {
    throw new ApiError(response.status, "No se pudo generar el archivo");
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filenameHint;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};
